import { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Button, Collapse, ListItem } from '@material-ui/core';
import ChevronDownIcon from '../icons/ChevronDown';
import ChevronRightIcon from '../icons/ChevronRight';
import { Tooltip } from '@mui/material';
import { gradients } from '../theme/tokens';
import { lightVeil, grey, toneAlpha } from '../theme/tones';

/*
 * Le rail est marine (le bleu du logotype) dans les deux modes : les couleurs
 * du menu sont donc des blancs en transparence, indépendants du thème.
 */
const IDLE = lightVeil(0.72);
const ACTIVE = grey(0);
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const T = `all 0.22s ${EASE}`;

/**
 * Pastille d'icône : c'est elle qui porte l'état (repos / survol / actif),
 * pas le texte. Le menu reste lisible même replié, où seule la pastille
 * subsiste.
 */
const iconChip = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  flexShrink: 0,
  borderRadius: '9px',
  backgroundColor: lightVeil(0.06),
  border: `1px solid ${lightVeil(0.08)}`,
  color: lightVeil(0.62),
  transition: T,
  '& svg': {
    fontSize: '1.05rem',
    transition: T
  }
};

const NavItem = (props) => {
  const { active, children, depth, icon, info, open: openProp, path, title, drawerOpen, openMobile, ...other } = props;
  const [open, setOpen] = useState(openProp);
  const expanded = drawerOpen || openMobile;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const indent = depth > 0 ? 12 + 10 * depth : 0;

  const baseSx = {
    color: IDLE,
    fontSize: '0.84rem',
    fontWeight: 500,
    letterSpacing: '0.005em',
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    width: '100%',
    minWidth: 0,
    gap: 1.25,
    pl: `${8 + indent}px`,
    pr: 1,
    py: 0.8,
    borderRadius: '11px',
    position: 'relative',
    overflow: 'hidden',
    transition: T,
    '& .nav-chip': iconChip,
    '&:hover': {
      backgroundColor: lightVeil(0.06),
      color: ACTIVE,
      '& .nav-chip': {
        backgroundColor: lightVeil(0.12),
        borderColor: lightVeil(0.16),
        color: ACTIVE,
        transform: 'translateY(-1px)'
      }
    }
  };

  // État actif : dégradé « aurora » atténué + rail vertical + pastille pleine.
  // Trois signaux redondants, lisibles même en périphérie.
  const activeSx = {
    color: ACTIVE,
    fontWeight: 700,
    background: `linear-gradient(90deg, ${toneAlpha('blue', 0.32)} 0%, ${toneAlpha('indigo', 0.16)} 45%, ${toneAlpha('blue', 0.03)} 100%)`,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '20%',
      bottom: '20%',
      width: 3,
      borderRadius: '0 3px 3px 0',
      background: gradients.accent
    },
    '& .nav-chip': {
      ...iconChip,
      background: gradients.accent,
      borderColor: 'transparent',
      color: ACTIVE,
      boxShadow: `0 6px 16px -6px ${toneAlpha('blue', 0.7)}`
    },
    '&:hover': {
      background: `linear-gradient(90deg, ${toneAlpha('blue', 0.4)} 0%, ${toneAlpha('indigo', 0.2)} 45%, ${toneAlpha('blue', 0.05)} 100%)`,
      color: ACTIVE
    }
  };

  const renderIcon = () => (
    <Box
      component="span"
      className="nav-chip"
    >
      {icon}
    </Box>
  );

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{ display: 'block', py: 0.2 }}
        {...other}
      >
        <Button
          onClick={handleToggle}
          disableRipple={false}
          sx={{
            ...baseSx,
            ...(active && { color: ACTIVE }),
            ...(!expanded && { justifyContent: 'center', pl: 0, pr: 0 })
          }}
          variant="text"
        >
          {renderIcon()}
          {expanded && (
            <>
              <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {title}
              </Box>
              {info}
              <Box
                component="span"
                sx={{ display: 'inline-flex', color: lightVeil(0.4) }}
              >
                {open ? <ChevronDownIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </Box>
            </>
          )}
        </Button>
        <Collapse in={open}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  // Leaf
  const leaf = (
    <Button
      component={path && RouterLink}
      sx={{
        ...baseSx,
        ...(!expanded && { justifyContent: 'center', pl: 0, pr: 0 }),
        ...(active && activeSx)
      }}
      variant="text"
      to={path}
      onClick={props.onClick}
    >
      {renderIcon()}
      {expanded && (
        <>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </Box>
          {info}
        </>
      )}
    </Button>
  );

  return (
    <ListItem
      disableGutters
      sx={{ display: 'flex', py: 0.2 }}
    >
      {expanded ? leaf : (
        <Tooltip title={title} placement="right" arrow>
          {leaf}
        </Tooltip>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
  drawerOpen: PropTypes.bool,
  openMobile: PropTypes.bool
};

NavItem.defaultProps = {
  active: false,
  open: false
};

export default NavItem;
